import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { analytics, crashlytics } from "./services/firebase";
// import TestPage from './pages/TestPage';

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children?: React.ReactNode },
  { hasError: boolean; error: any }
> {
  public props: { children?: React.ReactNode };
  state: { hasError: boolean; error: any };

  constructor(props: { children?: React.ReactNode }) {
    super(props);
    this.props = props;
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    this.sendErrorToCrashlytics(error, errorInfo);
  }

  sendErrorToCrashlytics(error: any, errorInfo?: any) {
    // 1. Set Contextual Keys
    crashlytics.setCustomKey("severity", "fatal");
    crashlytics.setCustomKey("timestamp", new Date().toISOString());
    crashlytics.setCustomKey("userAgent", navigator?.userAgent);
    crashlytics.setCustomKey("errorStack", error?.stack || "No stack");
    crashlytics.setCustomKey(
      "componentStack",
      errorInfo?.componentStack || "N/A",
    );
    crashlytics.setCustomKey("environment", process.env.NODE_ENV || "not_set");
    crashlytics.setCustomKey("url", window?.location?.href);

    // 2. Log the Error
    crashlytics.logError(
      error?.message || "empty",
      `React Boundary Error Caught`,
      error,
    );
    crashlytics.log(`React Boundary Error: ${error?.message || "empty"}`);
    analytics.logEvent("react_boundary_error", {
      timestamp: new Date().toISOString(),
      userAgent: navigator?.userAgent,
      url: window?.location?.href,
      message: error?.message || "empty",
      stack: error?.stack || "No stack",
      componentStack: errorInfo?.componentStack || "N/A",
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            padding: "20px",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <h1 style={{ color: "#7C3AED", marginBottom: "10px" }}>
            Oops! Something went wrong
          </h1>
          <p style={{ color: "#666", marginBottom: "20px" }}>
            We have reported this error to our team. Feel free to reach out to
            us at contact@whoisin.sg
          </p>
          <button
            onClick={() => window.location.replace("/")}
            style={{
              padding: "10px 20px",
              backgroundColor: "#7C3AED",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Global error handler to suppress gapi errors on Capacitor
if (typeof window !== "undefined") {
  // Suppress gapi iframe errors that occur on capacitor:// scheme
  window.addEventListener(
    "error",
    (event) => {
      if (
        event.message &&
        (event.message.includes("gapi") ||
          event.message.includes("apis.google.com") ||
          event.message.includes("Script error"))
      ) {
        // Optional: log non-fatal info that GAPI was suppressed
        // crashlytics.log("Suppressed GAPI error on Capacitor");
        event.preventDefault();
        return false;
      }
      // For all other non-GAPI global errors: a lot of logs
      // crashlytics.logError(event?.error || event?.message || 'empty', 'Global Window Error', new Error(event?.message || 'Global Window Error'));
    },
    true,
  );

  // Prevent unhandled promise rejections from Google APIs
  window.addEventListener("unhandledrejection", (event) => {
    if (event.reason && event.reason.toString().includes("gapi")) {
      event.preventDefault();
    }
    crashlytics.logError(
      event?.reason?.toString(),
      "Unhandled Promise Rejection",
      new Error(event?.reason?.toString() || "Unhandled Promise Rejection"),
    );
  });

  // Mock gapi to prevent initialization errors on Capacitor
  if (
    window.location.protocol === "capacitor:" ||
    window.location.hostname === "localhost"
  ) {
    (window as any).gapi = {
      load: () => {},
      iframes: {
        getContext: () => ({
          openChild: () => {},
          getOpenChildren: () => [],
        }),
      },
    };
  }
}

// Wait for CSS to be loaded before rendering
const renderApp = () => {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Could not find root element to mount to");
  }

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>,
  );

  // Remove static splash after React mounts
  const staticSplash = document.getElementById("static-splash");
  if (staticSplash) {
    staticSplash.style.display = "none";
  }
};

// Ensure DOM and styles are loaded
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", renderApp);
} else {
  renderApp();
}
